/**
 * Helpers pour l'exécution du SaaS en tant que Mini App Telegram.
 * Le script https://telegram.org/js/telegram-web-app.js est chargé dans public/index.html.
 */

export type TelegramWebApp = NonNullable<Window['Telegram']>['WebApp'];

export const getTelegramWebApp = (): TelegramWebApp | undefined =>
  typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

/** Vrai uniquement quand la page est ouverte depuis Telegram (initData signée présente). */
export const isTelegramMiniApp = (): boolean => {
  const wa = getTelegramWebApp();
  return !!wa && typeof wa.initData === 'string' && wa.initData.length > 0;
};

export const getTelegramInitData = (): string => getTelegramWebApp()?.initData || '';

/** Paramètre `startapp` (deep link t.me/bot/app?startapp=...) ou `start_param`. */
export const getTelegramStartParam = (): string | undefined =>
  getTelegramWebApp()?.initDataUnsafe?.start_param;

/** Route interne à ouvrir selon le start_param reçu depuis Telegram. */
export const resolveStartParamRoute = (startParam?: string): string | null => {
  switch ((startParam || '').toLowerCase()) {
    case 'lien':
    case 'link':
    case 'new_invoice':
    case 'invoice':
      return '/app/invoices/new';
    case 'dashboard':
    case 'app':
      return '/app/dashboard';
    case 'withdraw':
    case 'withdraws':
      return '/app/withdraws';
    case 'settings':
      return '/app/settings';
    case 'pro':
      return '/app/pro';
    default:
      return null;
  }
};

/** Prépare la Mini App (ready + plein écran). Sans effet hors Telegram. */
export const initTelegramMiniApp = () => {
  const wa = getTelegramWebApp();
  if (!wa || !isTelegramMiniApp()) return;
  try {
    wa.ready();
    wa.expand();
    document.body.classList.add('tg-mini-app');
  } catch {
    /* API indisponible */
  }
};

/** Ouvre la feuille de partage Telegram avec un lien et un texte. */
export const shareViaTelegram = (url: string, text?: string) => {
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}${
    text ? `&text=${encodeURIComponent(text)}` : ''
  }`;
  const wa = getTelegramWebApp();
  if (wa?.openTelegramLink) {
    wa.openTelegramLink(shareUrl);
  } else {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Ouvre une facture Telegram Stars : fenêtre native dans la Mini App, sinon nouvel onglet (lien t.me).
 * `onStatus` n'est appelé que dans la Mini App (le statut réel est de toute façon confirmé par l'API).
 */
export const openStarsInvoice = (
  invoiceLink: string,
  onStatus?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void,
) => {
  const wa = getTelegramWebApp();
  if (wa?.openInvoice && isTelegramMiniApp()) {
    wa.openInvoice(invoiceLink, onStatus);
  } else {
    window.open(invoiceLink, '_blank', 'noopener,noreferrer');
  }
};
